import React, { useState, useEffect } from 'react'
import { useNotificationActions } from '@/contexts/NotificationContext'
import { usePOSStore } from '@/store/posStore'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import type { Supplier } from '@/types/pos'

interface SuppliersPageProps {
  /** When true, hide the page title (e.g. when embedded in Procurement). */
  embedded?: boolean
}

export const SuppliersPage: React.FC<SuppliersPageProps> = ({ embedded = false }) => {
  const {
    suppliers,
    isLoading,
    fetchSuppliers,
    addSupplier,
    updateSupplier,
    deleteSupplier
  } = usePOSStore()

  const [showAddForm, setShowAddForm] = useState(false)
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null)
  const [viewingSupplier, setViewingSupplier] = useState<Supplier | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('All')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { showSuccess, showError } = useNotificationActions()

  useEffect(() => {
    fetchSuppliers()
  }, [fetchSuppliers])

  const filteredSuppliers = suppliers.filter(supplier => {
    const nameStr = supplier.name || ''
    const companyStr = supplier.company || ''
    const contactStr = supplier.contactPerson || ''
    const emailStr = supplier.email || ''
    const phoneStr = supplier.phone || ''
    const matchesSearch = nameStr.toLowerCase().includes(searchTerm.toLowerCase()) ||
      companyStr.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contactStr.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emailStr.toLowerCase().includes(searchTerm.toLowerCase()) ||
      phoneStr.includes(searchTerm)

    const status = supplier.isActive ? 'active' : 'inactive'
    const matchesStatus = filterStatus === 'All' || status === filterStatus
    return matchesSearch && matchesStatus
  })

  const getStatusColor = (isActive: boolean) => {
    return isActive
      ? 'text-green-400 bg-green-400/10 border-green-400/20'
      : 'text-muted-foreground bg-muted/10 border-border'
  }

  const handleAddSupplier = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    const formData = new FormData(e.target as HTMLFormElement)
    const supplierData = {
      name: formData.get('name') as string,
      contactPerson: formData.get('contactPerson') as string,
      company: formData.get('company') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      address: formData.get('address') as string,
      city: formData.get('city') as string,
      notes: formData.get('notes') as string,
    }

    try {
      await addSupplier(supplierData)
      showSuccess('Supplier Added', `${supplierData.name} has been registered successfully.`)
      setShowAddForm(false)
    } catch (error) {
      showError('Registration Failed', 'Could not save supplier information.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEditSupplier = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingSupplier) return

    setIsSubmitting(true)
    const formData = new FormData(e.target as HTMLFormElement)
    const updates = {
      name: formData.get('name') as string,
      contactPerson: formData.get('contactPerson') as string,
      company: formData.get('company') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      address: formData.get('address') as string,
      city: formData.get('city') as string,
      notes: formData.get('notes') as string,
    }

    try {
      const supplierId = editingSupplier.id || editingSupplier._id
      await updateSupplier(supplierId!, updates)
      showSuccess('Supplier Updated', `${updates.name}'s information has been updated.`)
      setEditingSupplier(null)
    } catch (error) {
      showError('Update Failed', 'Could not update supplier information.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteSupplier = async (supplier: Supplier) => {
    if (!confirm(`Are you sure you want to delete ${supplier.name}? This action cannot be undone.`)) {
      return
    }

    try {
      const supplierId = supplier.id || supplier._id
      await deleteSupplier(supplierId!)
      showSuccess('Supplier Deleted', `${supplier.name} has been removed from the system.`)
    } catch (error) {
      showError('Delete Failed', 'Could not delete supplier.')
    }
  }

  const handleToggleStatus = async (supplier: Supplier) => {
    try {
      const supplierId = supplier.id || supplier._id
      await updateSupplier(supplierId!, { isActive: !supplier.isActive })
      showSuccess(
        supplier.isActive ? 'Supplier Deactivated' : 'Supplier Activated',
        `${supplier.name} has been ${supplier.isActive ? 'deactivated' : 'activated'}.`
      )
    } catch (error) {
      showError('Update Failed', 'Could not update supplier status.')
    }
  }

  const renderSupplierForm = (supplier: Supplier | null, onSubmit: (e: React.FormEvent) => void, onCancel: () => void) => (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="glass-card w-full max-w-lg border-primary/30 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold esales-text mb-6">
          {supplier ? 'Edit Supplier' : 'Register New Supplier'}
        </h2>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Supplier / Business Name *</label>
              <input name="name" defaultValue={supplier?.name || ''} required className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" placeholder="e.g. ABC Distributors" />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Company</label>
              <input name="company" defaultValue={supplier?.company || ''} className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" placeholder="Parent company" />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Contact Person</label>
              <input name="contactPerson" defaultValue={supplier?.contactPerson || ''} className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" placeholder="John Doe" />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Email Address</label>
              <input name="email" type="email" defaultValue={supplier?.email || ''} className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" placeholder="supplier@example.com" />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Phone Number</label>
              <input name="phone" defaultValue={supplier?.phone || ''} className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" placeholder="+94 77 123 4567" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Address</label>
              <input name="address" defaultValue={supplier?.address || ''} className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" placeholder="123 Main Street" />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">City</label>
              <input name="city" defaultValue={supplier?.city || ''} className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" placeholder="Colombo" />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Notes</label>
              <input name="notes" defaultValue={supplier?.notes || ''} className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" placeholder="Any additional notes..." />
            </div>
          </div>

          <div className="flex gap-4 mt-8">
            <button type="button" onClick={onCancel} className="flex-1 py-4 bg-muted hover:bg-muted/80 rounded-xl font-bold text-xs uppercase tracking-widest transition-colors">
              {supplier ? 'Cancel' : 'Discard'}
            </button>
            <button type="submit" disabled={isSubmitting} className="flex-1 py-4 bg-primary text-white rounded-xl font-bold text-xs uppercase tracking-widest glow-effect transition-colors">
              {isSubmitting ? 'WORKING...' : supplier ? 'SAVE CHANGES' : 'REGISTER'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gradient-space p-3 sm:p-4 lg:p-6">
      <div className="w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6 sm:mb-8">
          {!embedded && (
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold esales-text font-display">Supplier Management</h1>
              <p className="text-muted-foreground mt-2">Manage your vendors, distributors, and supply chain partners.</p>
            </div>
          )}
          <button
            onClick={() => setShowAddForm(true)}
            className="glow-button"
          >
            <span className="mr-2">🏭</span> Add Supplier
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          {[
            { label: 'Total Suppliers', value: suppliers.length, icon: '🏭', color: 'text-primary' },
            { label: 'Active Suppliers', value: suppliers.filter(s => s.isActive).length, icon: '✅', color: 'text-green-400' },
            { label: 'Inactive', value: suppliers.filter(s => !s.isActive).length, icon: '⏸️', color: 'text-yellow-400' },
            { label: 'Total Purchases', value: `Rs.${suppliers.reduce((sum, s) => sum + (s.totalSpent || 0), 0).toLocaleString()}`, icon: '💰', color: 'text-blue-400' }
          ].map((stat, i) => (
            <div key={i} className="glass-card">
              <div className="flex justify-between items-start mb-2">
                <span className="text-2xl">{stat.icon}</span>
                <span className={`text-2xl font-bold ${stat.color}`}>{stat.value}</span>
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-widest font-bold font-sans">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="glass-card mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <input
                type="text"
                placeholder="Search suppliers by name, company, contact, email or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:outline-none"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 bg-background/50 border border-border rounded-lg"
            >
              <option value="All">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Suppliers Table */}

        {/* Mobile card view — visible only below md */}
        <div className="md:hidden space-y-3">
          {isLoading && suppliers.length === 0 ? (
            <LoadingSpinner
              size="md"
              message="Loading Suppliers"
              submessage="Syncing supplier database..."
            />
          ) : filteredSuppliers.length === 0 ? (
            <div className="glass-card py-12 text-center text-muted-foreground italic">
              {searchTerm || filterStatus !== 'All'
                ? 'No suppliers match your current filters.'
                : 'No suppliers registered yet. Click "Add Supplier" to get started.'}
            </div>
          ) : (
            filteredSuppliers.map(supplier => (
              <div key={supplier.id || supplier._id} className="glass-card space-y-3">
                {/* Name + status */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-foreground truncate">{supplier.name}</div>
                    {supplier.company && (
                      <div className="text-xs text-muted-foreground truncate">{supplier.company}</div>
                    )}
                  </div>
                  <button
                    onClick={() => handleToggleStatus(supplier)}
                    className={`shrink-0 px-3 py-1 text-[10px] font-black uppercase rounded-full border cursor-pointer transition-colors ${getStatusColor(supplier.isActive)}`}
                    title={`Click to ${supplier.isActive ? 'deactivate' : 'activate'}`}
                  >
                    {supplier.isActive ? 'active' : 'inactive'}
                  </button>
                </div>

                {/* Contact person */}
                {supplier.contactPerson && (
                  <div className="text-xs text-primary/70">
                    <span className="font-bold uppercase tracking-widest text-muted-foreground">Contact: </span>
                    {supplier.contactPerson}
                  </div>
                )}

                {/* Phone + email */}
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Phone</p>
                    <p className="mt-0.5">{supplier.phone || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Email</p>
                    <p className="mt-0.5 truncate">{supplier.email || '—'}</p>
                  </div>
                </div>

                {/* Address */}
                {(supplier.address || supplier.city) && (
                  <div className="text-sm">
                    <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Address</p>
                    <p className="mt-0.5 truncate text-muted-foreground">
                      {[supplier.address, supplier.city].filter(Boolean).join(', ')}
                    </p>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex items-center gap-3 pt-1 border-t border-border">
                  <button
                    onClick={() => setViewingSupplier(supplier)}
                    className="text-xs font-bold text-blue-400 hover:underline"
                  >
                    VIEW
                  </button>
                  <button
                    onClick={() => setEditingSupplier(supplier)}
                    className="text-xs font-bold text-primary hover:underline"
                  >
                    EDIT
                  </button>
                  <button
                    onClick={() => handleDeleteSupplier(supplier)}
                    className="text-xs font-bold text-red-400 hover:underline"
                  >
                    DELETE
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop table view — hidden below md */}
        <div className="hidden md:block glass-card overflow-hidden">
          <div className="overflow-x-auto">
            {isLoading && suppliers.length === 0 ? (
              <LoadingSpinner
                size="md"
                message="Loading Suppliers"
                submessage="Syncing supplier database..."
              />
            ) : (
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-border text-muted-foreground text-xs uppercase tracking-widest">
                    <th className="px-6 py-4 font-bold">Supplier Information</th>
                    <th className="px-6 py-4 font-bold">Contact Details</th>
                    <th className="px-6 py-4 font-bold">Location</th>
                    <th className="px-6 py-4 font-bold text-center">Orders</th>
                    <th className="px-6 py-4 font-bold">Total Purchases</th>
                    <th className="px-6 py-4 font-bold">Status</th>
                    <th className="px-6 py-4 font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {filteredSuppliers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-muted-foreground italic">
                        {searchTerm || filterStatus !== 'All'
                          ? 'No suppliers match your current filters.'
                          : 'No suppliers registered yet. Click "Add Supplier" to get started.'}
                      </td>
                    </tr>
                  ) : (
                    filteredSuppliers.map(supplier => (
                      <tr key={supplier.id || supplier._id} className="hover:bg-muted/10 transition-colors">
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-bold text-foreground">{supplier.name}</div>
                            <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {supplier.company || supplier.contactPerson || 'No details'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">{supplier.email || '—'}</div>
                          <div className="text-xs text-muted-foreground">{supplier.phone || '—'}</div>
                          {supplier.contactPerson && (
                            <div className="text-xs text-primary/70 mt-0.5">{supplier.contactPerson}</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">{supplier.city || '—'}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-[150px]">{supplier.address || ''}</div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="bg-muted px-2 py-1 rounded text-xs font-mono">{supplier.totalOrders || 0}</span>
                        </td>
                        <td className="px-6 py-4 font-bold text-primary">
                          Rs.{(supplier.totalSpent || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleToggleStatus(supplier)}
                            className={`px-3 py-1 text-[10px] font-black uppercase rounded-full border cursor-pointer transition-colors ${getStatusColor(supplier.isActive)}`}
                            title={`Click to ${supplier.isActive ? 'deactivate' : 'activate'}`}
                          >
                            {supplier.isActive ? 'active' : 'inactive'}
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => setViewingSupplier(supplier)}
                              className="text-xs font-bold text-blue-400 hover:underline"
                            >
                              VIEW
                            </button>
                            <button
                              onClick={() => setEditingSupplier(supplier)}
                              className="text-xs font-bold text-primary hover:underline"
                            >
                              EDIT
                            </button>
                            <button
                              onClick={() => handleDeleteSupplier(supplier)}
                              className="text-xs font-bold text-red-400 hover:underline"
                            >
                              DELETE
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Add Modal */}
        {showAddForm && renderSupplierForm(null, handleAddSupplier, () => setShowAddForm(false))}

        {/* Edit Modal */}
        {editingSupplier && renderSupplierForm(editingSupplier, handleEditSupplier, () => setEditingSupplier(null))}

        {/* View Modal */}
        {viewingSupplier && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="glass-card w-full max-w-lg border-primary/30">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold esales-text">{viewingSupplier.name}</h2>
                  {viewingSupplier.company && (
                    <p className="text-sm text-muted-foreground mt-1">{viewingSupplier.company}</p>
                  )}
                </div>
                <span className={`px-3 py-1 text-[10px] font-black uppercase rounded-full border ${getStatusColor(viewingSupplier.isActive)}`}>
                  {viewingSupplier.isActive ? 'active' : 'inactive'}
                </span>
              </div>

              <div className="space-y-4">
                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Contact Person</p>
                    <p className="text-sm text-foreground mt-1">{viewingSupplier.contactPerson || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Phone</p>
                    <p className="text-sm text-foreground mt-1">{viewingSupplier.phone || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Email</p>
                    <p className="text-sm text-foreground mt-1">{viewingSupplier.email || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">City</p>
                    <p className="text-sm text-foreground mt-1">{viewingSupplier.city || '—'}</p>
                  </div>
                </div>

                {/* Address */}
                {viewingSupplier.address && (
                  <div>
                    <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Address</p>
                    <p className="text-sm text-foreground mt-1">{viewingSupplier.address}</p>
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                  <div className="glass-card !p-4">
                    <p className="text-xs text-muted-foreground">Total Orders</p>
                    <p className="text-2xl font-bold text-primary">{viewingSupplier.totalOrders || 0}</p>
                  </div>
                  <div className="glass-card !p-4">
                    <p className="text-xs text-muted-foreground">Total Purchases</p>
                    <p className="text-2xl font-bold text-green-400">Rs.{(viewingSupplier.totalSpent || 0).toLocaleString()}</p>
                  </div>
                </div>

                {/* Notes */}
                {viewingSupplier.notes && (
                  <div>
                    <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Notes</p>
                    <p className="text-sm text-foreground mt-1 bg-muted/20 rounded-lg p-3">{viewingSupplier.notes}</p>
                  </div>
                )}

                {/* Meta */}
                <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                  <p>Registered: {viewingSupplier.createdAt ? new Date(viewingSupplier.createdAt as string).toLocaleDateString() : '—'}</p>
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  onClick={() => setViewingSupplier(null)}
                  className="flex-1 py-4 bg-muted hover:bg-muted/80 rounded-xl font-bold text-xs uppercase tracking-widest transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => { setViewingSupplier(null); setEditingSupplier(viewingSupplier); }}
                  className="flex-1 py-4 bg-primary text-white rounded-xl font-bold text-xs uppercase tracking-widest glow-effect transition-colors"
                >
                  Edit Supplier
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
